import { formatUccFilingClassificationLabel, uccFilingClassificationFields, type UccFilingClassificationField } from "./uccFilingCategories";
import { formatRelativeDateFilter } from "./uccRelativeDatePreset";

export type UccPayloadMode = "debtor" | "secured_party";

const NON_FILTER_PAYLOAD_KEYS = new Set([
	"page",
	"per_page",
	"exclude",
	"collateral_contains_operator",
	"collateral_match_mode",
	"collateral_split_words",
	"classification_description_match",
	"radius",
	"radius_unit",
	"sort_by",
	"sort_order",
	"saved_search_field",
]);

const FILTER_LABELS: Record<string, string> = {
	address: "Address",
	amendment_category: "Amendment Category",
	annual_revenue: "Annual Revenue",
	asset_brand: "Brand",
	asset_category: "Collateral Category",
	asset_equipment_subtype: "Equipment Subtype",
	asset_equipment_type: "Equipment Type",
	asset_industry: "Collateral Industry",
	collateral: "Collateral",
	company_filing_count: "Number of Filings",
	classification_description_exact_match: "Exact SIC/NAICS Description Match",
	company_name: "Debtor Name",
	date_expired: "Date Expired",
	date_filed: "Date Filed",
	terminated_date: "Termination Date",
	debtor_name: "Debtor Name",
	debtor_type: "Debtor Type",
	eight_digit_sic_code: "8-Digit SIC Code",
	eight_digit_sic_description: "8-Digit SIC Description",
	filing_number: "Filing Number",
	filing_status: "Filing Status",
	geographic_bounds: "Selected Map Area",
	four_digit_sic_code: "4-Digit SIC Code",
	four_digit_sic_description: "4-Digit SIC Description",
	has_amendment: "Has Amendment",
	has_assets: "Has Assets",
	has_collateral: "Has Collateral",
	has_contacts: "Has Contacts",
	has_continuation: "Has Continuation",
	has_website: "Has Website",
	industry: "Debtor Industry",
	is_active_filing: "Active Filings Only",
	modified_date: "Modified Date",
	naics_code: "NAICS Code",
	naics_description: "NAICS Description",
	near_zip_code: "ZIP Radius",
	number_employees: "Number of Employees",
	one_filing_per_debtor: "Latest Filing Per Debtor",
	phone: "Phone",
	saved_search_list_id: "Saved List",
	secured_party_class: "Secured Party Classification",
	secured_party_eight_digit_sic_code: "Secured Party 8-Digit SIC Code",
	secured_party_eight_digit_sic_description: "Secured Party 8-Digit SIC Description",
	secured_party_four_digit_sic_code: "Secured Party 4-Digit SIC Code",
	secured_party_four_digit_sic_description: "Secured Party 4-Digit SIC Description",
	secured_party_industry: "Secured Party Industry",
	secured_party_naics_code: "Secured Party NAICS Code",
	secured_party_naics_description: "Secured Party NAICS Description",
	secured_party_name: "Secured Party Name",
	secured_party_two_digit_sic_code: "Secured Party 2-Digit SIC Code",
	secured_party_two_digit_sic_description: "Secured Party 2-Digit SIC Description",
	state: "Debtor State",
	state_filing: "Debtor State Filing",
	time_in_business: "Time in Business",
	primary_ucc_filing_category: "Filing Category (Main Type Only)",
	ucc_filing_categories: "Filing Categories",
	collateral_scope: "Collateral Scope",
	collateral_asset_classes: "Collateral Asset Classes",
	two_digit_sic_code: "2-Digit SIC Code",
	two_digit_sic_description: "2-Digit SIC Description",
	url: "Website",
};

function isEmptyPayloadValue(value: unknown) {
	if (value === null || value === undefined) return true;
	if (typeof value === "string") return value.trim().length === 0;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === "object") return Object.keys(value).length === 0;
	return false;
}

function toDisplayValues(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.map((item) => String(item).trim()).filter(Boolean);
	}
	if (value === null || value === undefined) return [];
	const raw = String(value).trim();
	return raw ? [raw] : [];
}

function isDefaultDebtorTypeValue(value: unknown) {
	const values = toDisplayValues(value);
	return values.length === 2 && values.includes("Individual") && values.includes("Organization");
}

function formatDatePart(value: string) {
	const match = value.match(/(\d{4})-(\d{2})-(\d{2})/);
	if (!match) return value;
	return `${match[2]}-${match[3]}-${match[1]}`;
}

function formatDateRangeValue(value: unknown) {
	const raw = String(value ?? "").trim();
	if (!raw) return "";
	const parts = raw.split(",").map((part) => part.trim());
	const from = parts.find((part) => part.startsWith(">"))?.replace(/^>\s*/, "");
	const to = parts.find((part) => part.startsWith("<"))?.replace(/^<\s*/, "");
	if (from && to) return `${formatDatePart(from)} - ${formatDatePart(to)}`;
	if (from) return `From ${formatDatePart(from)}`;
	if (to) return `To ${formatDatePart(to)}`;
	return raw;
}

function formatNumericValue(key: string, value: number) {
	if (key === "annual_revenue") return `$${value.toLocaleString()}`;
	if (key === "time_in_business") return `${value.toLocaleString()} year${value === 1 ? "" : "s"}`;
	return value.toLocaleString();
}

function formatNumericRangeValue(key: string, value: unknown) {
	const values =
		value && typeof value === "object" && !Array.isArray(value)
			? [((value as Record<string, unknown>).min ?? (value as Record<string, unknown>).gte), ((value as Record<string, unknown>).max ?? (value as Record<string, unknown>).lte)]
					.map((item) => Number(item))
					.filter((item) => Number.isFinite(item))
			: Array.isArray(value)
			? value.map((item) => Number(item)).filter((item) => Number.isFinite(item))
			: [Number(value)].filter((item) => Number.isFinite(item));
	if (values.length === 0) return String(value ?? "");
	if (values.length === 1) return formatNumericValue(key, values[0]);
	return `${formatNumericValue(key, values[0])} - ${formatNumericValue(key, values[1])}`;
}

function formatAmendmentCategoryValue(value: unknown) {
	return toDisplayValues(value)
		.map((item) =>
			item
				.toLowerCase()
				.split("_")
				.map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
				.join(" ")
		)
		.join(", ");
}

function formatGeographicValue(payload: Record<string, unknown>) {
	const zip = toDisplayValues(payload.near_zip_code)[0] ?? "";
	const radius = Number(payload.radius);
	if (zip && Number.isFinite(radius)) return `${zip} within ${radius.toLocaleString("en-US", { maximumFractionDigits: 2 })} mi`;
	if (zip) return zip;
	return "";
}

function payloadFilterLabel(key: string, mode: UccPayloadMode) {
	if (key === "name") {
		return mode === "secured_party" ? "Secured Party Name" : "Debtor Name";
	}
	return FILTER_LABELS[key] ?? key.replace(/_/g, " ");
}

function payloadFilterValue(key: string, value: unknown, payload: Record<string, unknown>) {
	if (key === "near_zip_code") return formatGeographicValue(payload);
	if (key === "geographic_bounds" && value && typeof value === "object" && !Array.isArray(value)) {
		const bounds = value as Record<string, unknown>;
		const values = [bounds.south, bounds.west, bounds.north, bounds.east].map(Number);
		if (values.every(Number.isFinite)) {
			return `${values[0].toFixed(4)}, ${values[1].toFixed(4)} to ${values[2].toFixed(4)}, ${values[3].toFixed(4)}`;
		}
	}
	if (key === "date_filed" || key === "date_expired") {
		const relativePresetLabel = formatRelativeDateFilter(value);
		if (relativePresetLabel) return relativePresetLabel;
	}
	if (key === "date_filed" || key === "date_expired" || key === "terminated_date") return formatDateRangeValue(value);
	if (key === "amendment_category") return formatAmendmentCategoryValue(value);
	if (uccFilingClassificationFields.includes(key as UccFilingClassificationField)) {
		return toDisplayValues(value)
			.map((item) => formatUccFilingClassificationLabel(key as UccFilingClassificationField, item))
			.join(", ");
	}
	if (["annual_revenue", "number_employees", "time_in_business", "company_filing_count"].includes(key)) {
		return formatNumericRangeValue(key, value);
	}
	const values = toDisplayValues(value);
	if (key === "collateral" && values.length > 0) {
		const operator = payload.collateral_contains_operator === "AND" ? "AND" : "OR";
		return values.join(` ${operator} `);
	}
	return values.join(", ");
}

function excludedPayloadParts(value: unknown, mode: UccPayloadMode) {
	if (!value || typeof value !== "object" || Array.isArray(value)) return [];
	return Object.entries(value as Record<string, unknown>).flatMap(([key, excludedValues]) => {
		const values = toDisplayValues(excludedValues);
		const formatted = uccFilingClassificationFields.includes(key as UccFilingClassificationField)
			? values.map((item) => formatUccFilingClassificationLabel(key as UccFilingClassificationField, item)).join(", ")
			: values.join(", ");
		if (!formatted) return [];
		return [`Exclude ${payloadFilterLabel(key, mode)}: ${formatted}`];
	});
}

export function summarizeUccPayloadParts(payload: Record<string, unknown> | null | undefined, mode: UccPayloadMode): string[] {
	if (!payload || typeof payload !== "object") return [];

	const keys = Object.keys(payload).filter((key) => {
		if (NON_FILTER_PAYLOAD_KEYS.has(key)) return false;
		if (key === "classification_description_exact_match" && payload[key] !== true) return false;
		if (key.endsWith("_list") || key.endsWith("_range")) return false;
		if (isEmptyPayloadValue(payload[key])) return false;
		if (key === "debtor_type" && isDefaultDebtorTypeValue(payload[key])) return false;
		return true;
	});

	const includedParts = keys.map((key) => {
		const label = payloadFilterLabel(key, mode);
		const value = payload[key];
		if (key === "saved_search_list_id" || value === true) return label;
		const formatted = payloadFilterValue(key, value, payload);
		return formatted ? `${label}: ${formatted}` : label;
	});
	return [...includedParts, ...excludedPayloadParts(payload.exclude, mode)];
}

export function summarizeUccPayload(
	payload: Record<string, unknown> | null | undefined,
	mode: UccPayloadMode,
	options: { maxParts?: number; maxPartLength?: number } = {}
): string {
	const { maxParts = 3, maxPartLength = 42 } = options;
	const parts = summarizeUccPayloadParts(payload, mode).map((part) => (part.length > maxPartLength ? `${part.slice(0, maxPartLength)}…` : part));
	if (parts.length === 0) return "";
	const visible = parts.slice(0, maxParts);
	const hidden = parts.length - visible.length;
	return hidden > 0 ? `${visible.join(" · ")} +${hidden} more` : visible.join(" · ");
}

export function uccPayloadTooltip(title: string, payload: Record<string, unknown> | null | undefined, mode: UccPayloadMode): string {
	return [title, ...summarizeUccPayloadParts(payload, mode)].filter(Boolean).join("\n");
}
